// Order.jsx - UPDATED WITH ALL STATUSES
import { useEffect, useState } from "react";
import { Container, Table, Badge, Button, Modal, Spinner } from "react-bootstrap";
import { motion } from "framer-motion";
import { FaEye } from "react-icons/fa";
import "./order.css";
import Header from "../../component/header/header";
import Sidebar from "../../component/sidebar/sidebar";

const Order = () => {
  const [orders, setOrders] = useState([]);
  const [viewOrder, setViewOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem("token");
  const API = `${process.env.REACT_APP_API_URL}/admin`;

  /* ================= FETCH ORDERS ================= */
  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const res = await fetch(`${API}/orders`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.message);
        setOrders(data.orders);
      } catch (err) {
        alert(err.message || "Failed to load orders");
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [token]);

  /* ================= UPDATE STATUS ================= */
  const handleStatusChange = async (orderId, status) => {
    try {
      const res = await fetch(`${API}/orders/${orderId}/status`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ status }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message);

      setOrders((prev) =>
        prev.map((o) => (o._id === orderId ? data.order : o))
      );
    } catch (err) {
      alert(err.message);
    }
  };

  // Get badge color based on status
  const getStatusBadgeColor = (status) => {
    const colors = {
      'Pending': 'warning',
      'Processing': 'info',
      'Shipped': 'primary',
      'Delivered': 'success',
      'Cancelled': 'danger'
    };
    return colors[status] || 'secondary';
  };

  // Check if status change is allowed
  const canChangeStatus = (currentStatus) => {
    return currentStatus !== "Delivered" && currentStatus !== "Cancelled";
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="admin-layout">
          <Sidebar />
          <main className="admin-content">
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3">Loading orders...</p>
            </div>
          </main>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="admin-layout">
        <Sidebar />

        <main className="admin-content">
          <Container fluid>
            <motion.h4 
              className="mb-4"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              Orders Management
              <Badge bg="info" className="ms-3">
                {orders.length} Orders
              </Badge>
            </motion.h4>

            <div className="table-wrapper">
              <Table responsive bordered hover className="order-table">
                <thead>
                  <tr>
                    <th>Order ID</th>
                    <th>Customer</th>
                    <th>Products</th>
                    <th>Payment</th>
                    <th>Total</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {orders.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="text-center py-4 text-muted">
                        No orders found
                      </td>
                    </tr>
                  ) : (
                    orders.map((order) => (
                      <motion.tr 
                        key={order._id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.1 }}
                      >
                        <td className="fw-bold">{order._id}</td>

                        {/* ✅ NAME + EMAIL + PHONE */}
                        <td>
                          <div><b>{order.shippingAddress?.name || order.customer || "Guest"}</b></div>
                          <div className="text-muted small">
                            {order.shippingAddress?.email}
                          </div>
                          <div className="text-muted small">
                            {order.shippingAddress?.phone}
                          </div>
                        </td>

                        <td>
                          {order.items?.slice(0, 2).map((i) => i.name).join(", ")}
                          {order.items?.length > 2 && ` +${order.items.length - 2} more`}
                        </td>

                        <td>
                          <Badge bg="success">{order.paymentMethod || "COD"}</Badge>
                        </td>

                        <td className="fw-bold">₹{order.totalPrice}</td>

                        <td>
                          <Badge 
                            bg={getStatusBadgeColor(order.orderStatus)}
                            className="px-3 py-2"
                          >
                            {order.orderStatus}
                          </Badge>
                        </td>

                        <td>
                          <div className="d-flex gap-2 flex-wrap">
                            <select
                              className="form-select form-select-sm"
                              value={order.orderStatus}
                              disabled={!canChangeStatus(order.orderStatus)}
                              onChange={(e) =>
                                handleStatusChange(order._id, e.target.value)
                              }
                              style={{ minWidth: '130px' }}
                            >
                              <option value="Pending">Pending</option>
                              <option value="Processing">Processing</option>
                              <option value="Shipped">Shipped</option>
                              <option value="Delivered">Delivered</option>
                              <option value="Cancelled">Cancelled</option>
                            </select>

                            <Button 
                              size="sm" 
                              variant="outline-primary"
                              onClick={() => setViewOrder(order)}
                            >
                              <FaEye />
                            </Button>
                          </div>
                        </td>
                      </motion.tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          </Container>
        </main>
      </div>

      {/* ================= VIEW ORDER MODAL ================= */}
      <Modal show={!!viewOrder} onHide={() => setViewOrder(null)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <FaEye className="me-2" /> Order Details
          </Modal.Title>
        </Modal.Header>

        {viewOrder && (
          <Modal.Body>
            <div className="order-details-modal">
              {/* Order Info */}
              <div className="order-info-section mb-3">
                <h6>Order Information</h6>
                <div className="row">
                  <div className="col-md-6">
                    <p><b>Order ID:</b> {viewOrder._id}</p>
                    <p><b>Status:</b> 
                      <Badge 
                        bg={getStatusBadgeColor(viewOrder.orderStatus)}
                        className="ms-2"
                      >
                        {viewOrder.orderStatus}
                      </Badge>
                    </p>
                    <p><b>Payment Method:</b> {viewOrder.paymentMethod || "COD"}</p>
                  </div>
                  <div className="col-md-6">
                    <p><b>Created:</b> {new Date(viewOrder.createdAt).toLocaleString()}</p>
                    <p><b>Updated:</b> {new Date(viewOrder.updatedAt).toLocaleString()}</p>
                  </div>
                </div>
              </div>

              <hr />

              {/* Customer Info */}
              <div className="customer-info-section mb-3">
                <h6>Customer Details</h6>
                <p><b>Name:</b> {viewOrder.shippingAddress?.name || viewOrder.customer || "N/A"}</p>
                <p><b>Email:</b> {viewOrder.shippingAddress?.email || "N/A"}</p>
                <p><b>Phone:</b> {viewOrder.shippingAddress?.phone || "N/A"}</p>
                <p><b>Address:</b><br />
                  {viewOrder.shippingAddress?.address},<br />
                  {viewOrder.shippingAddress?.city}, {viewOrder.shippingAddress?.state} - {viewOrder.shippingAddress?.pincode}<br />
                  {viewOrder.shippingAddress?.country}
                </p>
              </div>

              <hr />

              {/* Products */}
              <div className="products-section">
                <h6>Products</h6>
                <Table responsive bordered size="sm">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Product</th>
                      <th>Qty</th>
                      <th>Price</th>
                      <th>Total</th>
                      <th>Company</th>
                    </tr>
                  </thead>
                  <tbody>
                    {viewOrder.items.map((item, idx) => (
                      <tr key={item._id || idx}>
                        <td>{idx + 1}</td>
                        <td>{item.name}</td>
                        <td>{item.quantity}</td>
                        <td>₹{item.price}</td>
                        <td>₹{(item.price * item.quantity).toFixed(2)}</td>
                        <td>{item.company || "N/A"}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="fw-bold">
                      <td colSpan="4" className="text-end">Total:</td>
                      <td>₹{viewOrder.totalPrice}</td>
                      <td></td>
                    </tr>
                    {viewOrder.adminCommission > 0 && (
                      <tr>
                        <td colSpan="4" className="text-end text-warning">Admin Commission:</td>
                        <td className="text-warning">₹{viewOrder.adminCommission}</td>
                        <td></td>
                      </tr>
                    )}
                  </tfoot>
                </Table>
              </div>
            </div>
          </Modal.Body>
        )}

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setViewOrder(null)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default Order;